import { useEffect, useState, useCallback } from 'react';
import api from '../services/ApiService';

type SyncItem = {
  id: number;
  action: string;
  entity_type: string;
  entity_id: number | null;
  status: 'pending' | 'applied' | 'failed' | 'skipped';
  error_msg: string | null;
  created_at: string;
  applied_at: string | null;
  created_by: string;
};

type Stats = {
  pending: number;
  applied: number;
  failed: number;
  skipped: number;
  last_sync: string | null;
};

const STATUS_COLORS: Record<string, string> = {
  pending: '#f59e0b',
  applied: '#10b981',
  failed:  '#ef4444',
  skipped: '#64748b',
};

const ACTION_ICONS: Record<string, string> = {
  create: '✚',
  update: '✎',
  delete: '✕',
  reload: '⟳',
};

function fmt(ts: string | null) {
  if (!ts) return '—';
  return new Date(ts).toLocaleString();
}

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div style={{
      background: 'rgba(30,41,59,0.7)',
      border: `1px solid ${color}30`,
      borderRadius: 12,
      padding: '16px 20px',
      minWidth: 110,
      flex: 1,
    }}>
      <div style={{ fontSize: 28, fontWeight: 800, color }}>{value}</div>
      <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2, textTransform: 'capitalize' }}>{label}</div>
    </div>
  );
}

export default function AsteriskSyncPanel() {
  const [items, setItems] = useState<SyncItem[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [filter, setFilter] = useState<string>('all');
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [msg, setMsg] = useState<{ text: string; ok: boolean } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, q] = await Promise.all([
        api.getSyncQueueStats(),
        api.getSyncQueue(filter === 'all' ? undefined : filter),
      ]);
      setStats(s);
      setItems(q);
    } catch (e: any) {
      setMsg({ text: e?.response?.data?.error || 'Failed to load sync queue', ok: false });
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
  }, [load]);

  async function syncNow() {
    setSyncing(true);
    setMsg(null);
    try {
      await api.triggerSyncNow();
      setMsg({ text: 'Sync triggered — processing pending items…', ok: true });
      setTimeout(load, 2000);
    } catch (e: any) {
      setMsg({ text: e?.response?.data?.error || 'Sync trigger failed', ok: false });
    } finally {
      setSyncing(false);
    }
  }

  async function retryItem(id: number) {
    try {
      await api.retrySyncItem(id);
      load();
    } catch (e: any) {
      setMsg({ text: e?.response?.data?.error || 'Retry failed', ok: false });
    }
  }

  async function deleteItem(id: number) {
    if (!window.confirm('Remove this sync item?')) return;
    try {
      await api.deleteSyncItem(id);
      load();
    } catch (e: any) {
      setMsg({ text: e?.response?.data?.error || 'Delete failed', ok: false });
    }
  }

  const filterTabs = ['all', 'pending', 'failed', 'applied', 'skipped'];

  return (
    <div style={{ padding: '28px 24px', maxWidth: 1100 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 24 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#f1f5f9' }}>Configuration Sync Queue</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 3 }}>
            Changes queued for FS3 · auto-syncs every 5 min
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button
          onClick={load}
          disabled={loading}
          style={{
            background: 'rgba(30,41,59,0.8)', border: '1px solid rgba(100,116,139,0.3)',
            color: '#94a3b8', borderRadius: 8, padding: '8px 14px',
            fontSize: 13, cursor: 'pointer',
          }}
        >
          {loading ? '…' : '↻ Refresh'}
        </button>
        <button
          onClick={syncNow}
          disabled={syncing || (stats?.pending === 0)}
          style={{
            background: syncing ? '#1d4ed8' : 'linear-gradient(135deg, #8b5cf6, #6366f1)',
            border: 'none', color: 'white', borderRadius: 8,
            padding: '8px 18px', fontSize: 13, fontWeight: 700,
            cursor: syncing || stats?.pending === 0 ? 'not-allowed' : 'pointer',
            opacity: stats?.pending === 0 ? 0.5 : 1,
          }}
        >
          {syncing ? 'Syncing…' : '⟳ Sync Now'}
        </button>
      </div>

      {/* Feedback */}
      {msg && (
        <div style={{
          background: msg.ok ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)',
          border: `1px solid ${msg.ok ? '#10b981' : '#ef4444'}40`,
          borderRadius: 8, padding: '10px 14px',
          color: msg.ok ? '#6ee7b7' : '#fca5a5',
          fontSize: 13, marginBottom: 18,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <span>{msg.text}</span>
          <span style={{ cursor: 'pointer', opacity: 0.7 }} onClick={() => setMsg(null)}>✕</span>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
          <StatCard label="Pending"  value={stats.pending}  color="#f59e0b" />
          <StatCard label="Applied"  value={stats.applied}  color="#10b981" />
          <StatCard label="Failed"   value={stats.failed}   color="#ef4444" />
          <StatCard label="Skipped"  value={stats.skipped}  color="#64748b" />
          <div style={{
            background: 'rgba(30,41,59,0.7)',
            border: '1px solid rgba(100,116,139,0.2)',
            borderRadius: 12, padding: '16px 20px',
            flex: 2, minWidth: 180,
          }}>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>Last Sync</div>
            <div style={{ fontSize: 14, color: '#e2e8f0', fontWeight: 600 }}>
              {stats.last_sync ? fmt(stats.last_sync) : 'Never'}
            </div>
          </div>
        </div>
      )}

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
        {filterTabs.map(t => (
          <button
            key={t}
            onClick={() => setFilter(t)}
            style={{
              background: filter === t ? 'rgba(139,92,246,0.2)' : 'rgba(30,41,59,0.5)',
              border: `1px solid ${filter === t ? '#8b5cf6' : 'rgba(100,116,139,0.2)'}`,
              color: filter === t ? '#c4b5fd' : '#94a3b8',
              borderRadius: 6, padding: '5px 14px',
              fontSize: 12, fontWeight: filter === t ? 700 : 400,
              cursor: 'pointer', textTransform: 'capitalize',
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {/* Table */}
      <div style={{
        background: 'rgba(15,23,42,0.6)',
        border: '1px solid rgba(100,116,139,0.15)',
        borderRadius: 12,
        overflow: 'hidden',
      }}>
        {items.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#475569', fontSize: 14 }}>
            {loading ? 'Loading…' : `No ${filter === 'all' ? '' : filter + ' '}sync items.`}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'rgba(30,41,59,0.8)', borderBottom: '1px solid rgba(100,116,139,0.15)' }}>
                {['#', 'Action', 'Entity', 'ID', 'Status', 'Created', 'Applied', ''].map(h => (
                  <th key={h} style={{
                    padding: '10px 14px', textAlign: 'left',
                    color: '#64748b', fontWeight: 600, fontSize: 11,
                    textTransform: 'uppercase', letterSpacing: '0.5px',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <tr
                  key={item.id}
                  style={{
                    borderBottom: '1px solid rgba(100,116,139,0.08)',
                    background: i % 2 === 0 ? 'transparent' : 'rgba(30,41,59,0.2)',
                  }}
                >
                  <td style={{ padding: '10px 14px', color: '#475569', fontFamily: 'monospace' }}>
                    {item.id}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{
                      display: 'inline-flex', alignItems: 'center', gap: 5,
                      background: 'rgba(100,116,139,0.1)',
                      borderRadius: 4, padding: '2px 8px',
                      color: '#cbd5e1', fontSize: 12, fontWeight: 600,
                    }}>
                      <span>{ACTION_ICONS[item.action] ?? '?'}</span>
                      <span style={{ textTransform: 'capitalize' }}>{item.action}</span>
                    </span>
                  </td>
                  <td style={{ padding: '10px 14px', color: '#e2e8f0', fontWeight: 500 }}>
                    {item.entity_type.replace(/_/g, ' ')}
                  </td>
                  <td style={{ padding: '10px 14px', color: '#94a3b8', fontFamily: 'monospace' }}>
                    {item.entity_id ?? '—'}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{
                      display: 'inline-block',
                      background: `${STATUS_COLORS[item.status]}18`,
                      border: `1px solid ${STATUS_COLORS[item.status]}40`,
                      color: STATUS_COLORS[item.status],
                      borderRadius: 4, padding: '2px 8px',
                      fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
                    }}>
                      {item.status}
                    </span>
                    {item.status === 'failed' && item.error_msg && (
                      <div style={{ fontSize: 11, color: '#f87171', marginTop: 3, maxWidth: 200 }}
                           title={item.error_msg}>
                        {item.error_msg.slice(0, 60)}{item.error_msg.length > 60 ? '…' : ''}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: '10px 14px', color: '#64748b', fontSize: 12 }}>
                    {fmt(item.created_at)}
                  </td>
                  <td style={{ padding: '10px 14px', color: '#64748b', fontSize: 12 }}>
                    {fmt(item.applied_at)}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {item.status === 'failed' && (
                        <button
                          onClick={() => retryItem(item.id)}
                          title="Retry"
                          style={{
                            background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)',
                            color: '#6ee7b7', borderRadius: 6,
                            padding: '4px 10px', fontSize: 11, cursor: 'pointer',
                          }}
                        >
                          Retry
                        </button>
                      )}
                      <button
                        onClick={() => deleteItem(item.id)}
                        title="Remove"
                        style={{
                          background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)',
                          color: '#f87171', borderRadius: 6,
                          padding: '4px 8px', fontSize: 11, cursor: 'pointer',
                        }}
                      >
                        ✕
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div style={{ marginTop: 12, fontSize: 11, color: '#334155' }}>
        Showing {items.length} item{items.length !== 1 ? 's' : ''}
        {filter !== 'all' ? ` · filter: ${filter}` : ''} · auto-refresh every 30s
      </div>
    </div>
  );
}
